"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.createGenerateTools = createGenerateTools;
const fsExtra = __importStar(require("fs-extra"));
const dist_1 = require("../../../fs/dist");
const weaveIdea_1 = require("../../../app/dist/weaveIdea");
const weaveDesign_1 = require("../../../app/dist/weaveDesign");
const weavePlan_1 = require("../../../app/dist/weavePlan");
const req_1 = require("../../../app/dist/req");
const context_1 = require("../resources/context");
const sampling_1 = require("../sampling");
const appendToChat_1 = require("./appendToChat");
function msg(role, text) {
    return { role, content: { type: 'text', text } };
}
async function loadExtraContext(root, ids) {
    const parts = await Promise.all(ids.map(async (id) => {
        try {
            const fp = await (0, dist_1.findDocumentById)(root, id);
            if (!fp)
                return null;
            const doc = await (0, dist_1.loadDoc)(fp);
            return `### ${doc.title} (${doc.type})\n\n${doc.content ?? ''}`;
        }
        catch {
            return null;
        }
    }));
    return parts.filter(Boolean).join('\n\n---\n\n');
}
function makeTool(name, description, inputSchema, handler) {
    return {
        toolDef: { name, description, inputSchema },
        handle: async (root, args) => ({
            content: [{ type: 'text', text: JSON.stringify(await handler(root, args)) }],
        }),
    };
}
function createGenerateTools(server) {
    return [
        makeTool('loom_generate_idea', 'Generate a Loom idea document using AI sampling. Creates the doc and writes the AI-generated body. Requires sampling support from the MCP client.', {
            type: 'object',
            properties: {
                weave_slug: { type: 'string', description: 'Target weave folder slug' },
                thread_ulid: { type: 'string', description: 'Stable th_ ULID of the target thread (optional)' },
                title: { type: 'string', description: 'Title for the new idea doc' },
                prompt: { type: 'string', description: 'Description of the idea to generate' },
            },
            required: ['weave_slug', 'title', 'prompt'],
        }, async (root, args) => {
            const weaveId = args['weave_slug'];
            const threadId = args['thread_ulid'];
            const title = args['title'];
            const prompt = args['prompt'];
            const body = await (0, sampling_1.requestSampling)(server, [msg('user', `Draft a Loom idea document for the following:\n\n${prompt}\n\nWrite only the markdown body — no frontmatter. Start with a problem statement.`)], 'You are a Loom document author. Write concise, focused Loom idea documents.');
            const { id, filePath } = await (0, weaveIdea_1.weaveIdea)({ title, weaveSlug: weaveId, threadUlid: threadId }, { getActiveLoomRoot: () => (0, dist_1.getActiveLoomRoot)(root), saveDoc: dist_1.saveDoc, loadDoc: dist_1.loadDoc, fs: fsExtra });
            const doc = await (0, dist_1.loadDoc)(filePath);
            await (0, dist_1.saveDoc)({ ...doc, content: body, version: doc.version + 1 }, filePath);
            return { id, filePath };
        }),
        makeTool('loom_generate_design', 'Generate a Loom design document from thread context using AI sampling. Requires sampling support from the MCP client.', {
            type: 'object',
            properties: {
                weave_slug: { type: 'string', description: 'Target weave folder slug' },
                thread_ulid: { type: 'string', description: 'Stable th_ ULID of the target thread' },
                title: { type: 'string', description: 'Title for the new design doc' },
                context_ids: { type: 'array', items: { type: 'string' }, description: 'Optional. Additional doc IDs to inject as context.' },
            },
            required: ['weave_slug', 'thread_ulid', 'title'],
        }, async (root, args) => {
            const weaveId = args['weave_slug'];
            const threadId = args['thread_ulid'];
            const title = args['title'];
            const contextIds = Array.isArray(args['context_ids']) ? args['context_ids'] : [];
            const messages = [];
            try {
                const ctx = await (0, context_1.handleContextResource)(root, `loom://context/thread/${weaveId}/${threadId}`);
                messages.push(msg('user', `Thread context:\n\n${ctx.contents[0].text}`));
            }
            catch { /* best-effort */ }
            if (contextIds.length > 0) {
                const extra = await loadExtraContext(root, contextIds);
                if (extra)
                    messages.push(msg('user', `Additional context:\n\n${extra}`));
            }
            messages.push(msg('user', [
                `Draft a Loom design document titled "${title}". Write only the markdown body — no frontmatter.`,
                'Include: Goal, Architecture (high-level structure), Key Decisions (with rationale), Open Questions.',
                'Do NOT include a "Next Steps" or "Implementation Steps" section — those belong in the plan, not the design.',
                'Do NOT pre-decompose implementation into a numbered list. Designs describe *what* and *why*; plans describe *how* and *in what order*.',
                'Respect scope exclusions from the idea/chat — if the user said "no JS" or "no responsive QA", do not introduce them in the design.',
            ].join('\n')));
            const body = await (0, sampling_1.requestSampling)(server, messages, 'You are a Loom document author. Write detailed, structured Loom design documents.');
            const { id, filePath } = await (0, weaveDesign_1.weaveDesign)({ weaveSlug: weaveId, title, threadUlid: threadId }, { getActiveLoomRoot: () => (0, dist_1.getActiveLoomRoot)(root), saveDoc: dist_1.saveDoc, loadDoc: dist_1.loadDoc, fs: fsExtra });
            const doc = await (0, dist_1.loadDoc)(filePath);
            await (0, dist_1.saveDoc)({ ...doc, content: body, version: doc.version + 1 }, filePath);
            return { id, filePath };
        }),
        makeTool('loom_generate_plan', 'Generate a Loom implementation plan from thread context using AI sampling. Returns the created plan with steps. Requires sampling support from the MCP client.', {
            type: 'object',
            properties: {
                weave_slug: { type: 'string', description: 'Target weave folder slug' },
                thread_ulid: { type: 'string', description: 'Stable th_ ULID of the target thread' },
                title: { type: 'string', description: 'Title for the new plan doc' },
                context_ids: { type: 'array', items: { type: 'string' }, description: 'Optional. Additional doc IDs to inject as context.' },
            },
            required: ['weave_slug', 'thread_ulid', 'title'],
        }, async (root, args) => {
            const weaveId = args['weave_slug'];
            const threadId = args['thread_ulid'];
            const title = args['title'];
            const contextIds = Array.isArray(args['context_ids']) ? args['context_ids'] : [];
            const messages = [];
            try {
                const ctx = await (0, context_1.handleContextResource)(root, `loom://context/thread/${weaveId}/${threadId}`);
                messages.push(msg('user', `Thread context:\n\n${ctx.contents[0].text}`));
            }
            catch { /* best-effort */ }
            if (contextIds.length > 0) {
                const extra = await loadExtraContext(root, contextIds);
                if (extra)
                    messages.push(msg('user', `Additional context:\n\n${extra}`));
            }
            messages.push(msg('user', [
                `Generate an implementation plan for "${title}".`,
                '',
                'Rules:',
                '- Steps map to **deliverables**, not design subsections. If the idea or design names a "Deliverables" list, use it as the step skeleton.',
                '- Aim for the smallest step count that ships every deliverable. 2-4 steps is normal; >5 is suspect — collapse fine-grained sub-tasks into the deliverable they belong to.',
                '- Each step description names a concrete output (e.g. "Create pricing.html with three-tier markup and inline CSS, Pro highlighted") not a sub-decision (e.g. "Add box-shadow to Pro tier").',
                '- Do NOT invent QA, testing, accessibility-review, responsive-check, or post-implementation review steps unless the design names them as explicit deliverables.',
                '- Respect scope exclusions stated in the idea or chat (e.g. "no JS", "no responsive QA") — do not add steps for excluded work.',
                '- If the thread has a locked req (it appears first in the context), treat its ❌ Excluded items and ⛓ Constraints as HARD BOUNDARIES, cover every ✅ Included requirement, and cite the requirement ids (IN/C handles) each step advances in `satisfies`.',
                '',
                'Return ONLY a JSON array of steps — no prose, no markdown fences:',
                '[{"order":1,"description":"...","satisfies":["IN1"]},{"order":2,"description":"...","satisfies":[]}]',
            ].join('\n')));
            const generated = await (0, sampling_1.requestSampling)(server, messages, 'You are a Loom document author. Output ONLY valid JSON arrays for implementation plans.', 8192);
            let steps = [];
            try {
                const jsonMatch = generated.match(/\[[\s\S]*\]/);
                if (jsonMatch)
                    steps = JSON.parse(jsonMatch[0]);
            }
            catch {
                steps = [{ order: 1, description: `Generated plan:\n\n${generated}` }];
            }
            // Hand the generated steps to weavePlan as structured data (not a markdown
            // table), so the plan is born frontmatter-native and Loom owns the table.
            const stepsInput = steps.map((s) => ({
                description: s.description ?? '',
                satisfies: Array.isArray(s.satisfies) ? s.satisfies : [],
            }));
            const { id, filePath } = await (0, weavePlan_1.weavePlan)({ weaveSlug: weaveId, title, threadUlid: threadId, goal: title, steps: stepsInput }, { loadWeave: dist_1.loadWeave, saveDoc: dist_1.saveDoc, loadDoc: dist_1.loadDoc, fs: fsExtra, loomRoot: root });
            return { id, filePath, steps };
        }),
        makeTool('loom_generate_reference', 'Generate the body of a reference document using AI sampling. Reads the doc created by loom_create_reference, optionally loads thread context, generates content, and saves it. Requires sampling support.', {
            type: 'object',
            properties: {
                id: { type: 'string', description: 'Reference document ID (from loom_create_reference)' },
                weave_slug: { type: 'string', description: 'Optional weave folder slug to load for additional context' },
                thread_ulid: { type: 'string', description: 'Optional thread ULID to load for additional context' },
                context_ids: { type: 'array', items: { type: 'string' }, description: 'Optional additional doc IDs to load as context' },
            },
            required: ['id'],
        }, async (root, args) => {
            const id = args['id'];
            const weaveId = args['weave_slug'];
            const threadId = args['thread_ulid'];
            const contextIds = Array.isArray(args['context_ids']) ? args['context_ids'] : [];
            // Primary (agent-supplied) id → suggest-on-miss.
            const { filePath: fp } = await (0, dist_1.resolveDocIdOrThrow)(root, id);
            const doc = await (0, dist_1.loadDoc)(fp);
            const messages = [];
            if (weaveId && threadId) {
                try {
                    const ctx = await (0, context_1.handleContextResource)(root, `loom://context/thread/${weaveId}/${threadId}`);
                    messages.push(msg('user', `Thread context:\n\n${ctx.contents[0].text}`));
                }
                catch { /* best-effort */ }
            }
            if (contextIds.length > 0) {
                const extra = await loadExtraContext(root, contextIds);
                if (extra)
                    messages.push(msg('user', `Additional context:\n\n${extra}`));
            }
            const refTitle = doc.title;
            const refDescription = doc.description ?? '';
            messages.push(msg('user', [
                `Write a complete reference document titled "${refTitle}".`,
                refDescription ? `Description: ${refDescription}` : '',
                'Write only the markdown body — no frontmatter.',
                'Use clear headings, concise prose, and diagrams (ASCII or Mermaid) where helpful.',
                'This is a reference document — write authoritatively and factually.',
            ].filter(Boolean).join('\n')));
            const body = await (0, sampling_1.requestSampling)(server, messages, 'You are a technical writer creating structured reference documents for software projects.');
            await (0, dist_1.saveDoc)({ ...doc, content: body, version: doc.version + 1 }, fp);
            return { id, filePath: fp };
        }),
        makeTool('loom_generate_req', "Generate a thread's req (requirements) doc from its chat using AI sampling — faithfully extracts the user's explicitly-stated Included / Excluded / Constraints into a draft req.md. Requires sampling support: works in the VS Code extension; blocked in Claude Code CLI sessions (where the agent should extract and call loom_create_req with `content` instead).", {
            type: 'object',
            properties: {
                weave_slug: { type: 'string', description: 'Target weave folder slug' },
                thread_ulid: { type: 'string', description: 'Stable th_ ULID of the target thread' },
                title: { type: 'string', description: 'Optional title for the req doc' },
                context_ids: { type: 'array', items: { type: 'string' }, description: 'Optional. Additional doc IDs to inject as context.' },
            },
            required: ['weave_slug', 'thread_ulid'],
        }, async (root, args) => {
            const weaveId = args['weave_slug'];
            const threadId = args['thread_ulid'];
            const title = args['title'];
            const contextIds = Array.isArray(args['context_ids']) ? args['context_ids'] : [];
            const messages = [];
            try {
                const ctx = await (0, context_1.handleContextResource)(root, `loom://context/thread/${weaveId}/${threadId}`);
                messages.push(msg('user', `Thread context (the chat especially):\n\n${ctx.contents[0].text}`));
            }
            catch { /* best-effort */ }
            if (contextIds.length > 0) {
                const extra = await loadExtraContext(root, contextIds);
                if (extra)
                    messages.push(msg('user', `Additional context:\n\n${extra}`));
            }
            messages.push(msg('user', [
                'Extract the requirements the user has EXPLICITLY stated for this thread into a Loom req doc body.',
                'Output ONLY markdown (no frontmatter) with exactly these three sections, in this order:',
                '',
                '### ✅ Included',
                '### ❌ Excluded',
                '### ⛓ Constraints',
                '',
                'Under each, write bullet points. PREFIX every bullet with an inline-code stable id:',
                '- Included → `IN1`, `IN2`, …   Excluded → `EX1`, `EX2`, …   Constraints → `C1`, `C2`, …',
                'Example:  - `EX1` **Interaction testing** — no manual smoke-test steps.',
                '',
                'Extract ONLY what the user explicitly stated — do NOT invent scope, and do NOT treat open questions as requirements.',
                'Each ✅ Included item must be orthogonal and individually verifiable: do not restate one requirement from two angles, and do not add the overall outcome/thesis as an Included item (that is the goal, not a deliverable). Prefer fewer, sharper items over many overlapping ones.',
                'If a section has no stated items, leave it empty (keep the heading).',
            ].join('\n')));
            const body = await (0, sampling_1.requestSampling)(server, messages, 'You are a Loom requirements extractor. Faithfully pull the stated includes/excludes/constraints from the conversation; never invent scope.');
            const { id, filePath } = await (0, req_1.createReq)({ weaveSlug: weaveId, threadUlid: threadId, title, content: body }, { getActiveLoomRoot: () => (0, dist_1.getActiveLoomRoot)(root), saveDoc: dist_1.saveDoc, loadDoc: dist_1.loadDoc, fs: fsExtra });
            return { id, filePath };
        }),
        makeTool('loom_generate_chat_reply', 'Generate an AI reply for a Loom chat document using sampling. Appends the reply under "## AI:". Requires sampling support from the MCP client.', {
            type: 'object',
            properties: {
                chat_ulid: { type: 'string', description: 'Chat document ULID' },
            },
            required: ['chat_ulid'],
        }, async (root, args) => {
            const chatId = args['chat_ulid'];
            // Primary (agent-supplied) id → suggest-on-miss.
            const { filePath } = await (0, dist_1.resolveDocIdOrThrow)(root, chatId);
            const chatContent = await fsExtra.readFile(filePath, 'utf8');
            const reply = await (0, sampling_1.requestSampling)(server, [msg('user', chatContent)], `You are an AI assistant in a Loom design chat. This is a DISCUSSION surface only.

RULES — strictly enforced:
- Do NOT promise to implement anything. Never say "let me find X", "I'll update Y", "I'll check Z", "Let me look at the code", or any phrase that implies you are about to act.
- Do NOT write code changes, file edits, or reference specific lines as if about to modify them.
- Your role is to think through the problem, discuss design options, and identify what work is needed.
- If implementation is needed, say so explicitly and suggest the user promote this chat to a plan so that a DoStep can carry it out.
- Write a focused, constructive response that advances the conversation.`);
            // Route the write through loom_append_to_chat so chat mutations
            // funnel through one code path (no direct file writes here).
            await (0, appendToChat_1.handle)(root, { id: chatId, role: 'ai', body: reply });
            return { id: chatId, filePath };
        }),
    ];
}
